import { get, set } from 'lodash-es';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useVoiceSTT, useVoiceTTS } from '@/hooks/useVoiceProviders';
import {
  defaultConfig,
  MODEL_FIELD,
  supportsLanguage,
  ttsModelsForLanguage,
} from '@/modules/aiAgents/components/SpeechStrategy/helpers';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';
import { SPEECH_PROVIDER_NAMES } from '@/modules/developerTools/pages/speechModels/helper';
import { SpeechModelProvider } from '@/modules/developerTools/types';
import { SpeechKind, SpeechStrategyStep } from '@/shared/types/speechStrategy';
import { TTSProvider } from '@/shared/types/voiceProviders';

const HOSTED_PREFIX = 'moveo:';

/** The source value of a row that uses the platform default. */
export const PLATFORM_DEFAULT = 'none';

export interface SpeechSource {
  value: string;
  label: string;
  provider: SpeechModelProvider;
  hosted: boolean;
  /** The speech model's name, for a source that uses the account's own key. */
  keyName?: string;
}

/** The speech models of the account, then the providers Moveo hosts, that serve the kind. */
const useSpeechSources = (kind: SpeechKind): SpeechSource[] => {
  const { t } = useTranslation();
  const { speechModels, providers: catalog } = useSpeechModels();

  const forKind = catalog.filter((entry) => entry.provider && entry[kind].length > 0);
  const own = speechModels
    .filter((model) => forKind.some((entry) => entry.provider === model.provider))
    .map((model) => ({
      value: model.speech_model_id,
      label: `${model.name} · ${SPEECH_PROVIDER_NAMES[model.provider]}`,
      provider: model.provider,
      hosted: false,
      keyName: model.name,
    }));
  const hosted = forKind
    .filter((entry) => entry.hosted[kind])
    .map((entry) => ({
      value: `${HOSTED_PREFIX}${entry.provider}`,
      label: t('ai_agents.speech_strategy.moveo_provider', {
        0: SPEECH_PROVIDER_NAMES[entry.provider],
      }),
      provider: entry.provider,
      hosted: true,
    }));
  return [...own, ...hosted];
};

interface Params {
  kind: SpeechKind;
  /** Form path of the row's step, e.g. `tts.1.step`. */
  name: string;
  language: string;
}

/**
 * One language row of a speech strategy: the sources it can pick, the models of the picked
 * provider that serve the row's language, and the writes that keep the step consistent.
 */
export const useSpeechStep = ({ kind, name, language }: Params) => {
  const { control, setValue } = useFormContext();
  const step = useWatch({ control, name }) as SpeechStrategyStep | null;
  const sources = useSpeechSources(kind);
  const { providers: catalog } = useSpeechModels();
  const { models: allSttModels, status: sttCatalogStatus } = useVoiceSTT(
    step?.speech_model_id ?? undefined
  );
  // The default of a new source needs only the language of each model id; the hosted catalog has it
  const { getModelsByProvider: getHostedTtsModels } = useVoiceTTS();
  const { getModelsByProvider: getStepTtsModels, status: ttsCatalogStatus } = useVoiceTTS(
    undefined,
    step?.speech_model_id ?? undefined
  );

  const configPath = `${name}.config`;
  const provider = step?.provider;
  const modelField = provider ? MODEL_FIELD[kind][provider] : undefined;
  const modelId = modelField ? (get(step?.config, modelField) as string | undefined) : undefined;

  const sourceValue = step
    ? (step.speech_model_id ?? `${HOSTED_PREFIX}${step.provider}`)
    : PLATFORM_DEFAULT;

  const selectSource = (value: string) => {
    if (value === PLATFORM_DEFAULT) {
      setValue(name, null, { shouldDirty: true });
      return;
    }
    const source = sources.find((option) => option.value === value);
    if (!source) {
      return;
    }
    const config = defaultConfig({ kind, provider: source.provider, language, catalog });
    const ttsField = kind === 'tts' ? MODEL_FIELD.tts[source.provider] : undefined;
    if (ttsField) {
      // The catalog lists ids only; the voice catalog knows which models speak the row's language
      const ttsProvider = source.provider as TTSProvider;
      const compatible = ttsModelsForLanguage(
        getHostedTtsModels(ttsProvider),
        ttsProvider,
        language,
        catalog
      );
      if (compatible[0] && !compatible.some((model) => model.id === get(config, ttsField))) {
        set(config, ttsField, compatible[0].id);
      }
    }
    setValue(
      name,
      {
        speech_model_id: value.startsWith(HOSTED_PREFIX) ? null : value,
        provider: source.provider,
        config,
      },
      { shouldDirty: true }
    );
  };

  const selectModel = (nextModel: string) =>
    setValue(`${configPath}.${modelField}`, nextModel, { shouldDirty: true });

  const ttsModels =
    provider && kind === 'tts'
      ? ttsModelsForLanguage(
          getStepTtsModels(provider as TTSProvider),
          provider as TTSProvider,
          language,
          catalog
        )
      : [];
  const sttModels =
    provider && kind === 'stt'
      ? (allSttModels ?? []).filter(
          (model) =>
            model.provider === provider &&
            supportsLanguage({ kind, provider, model: model.id, language })
        )
      : [];

  return {
    step,
    configPath,
    sources,
    sourceValue,
    selectSource,
    hasModelField: !!modelField,
    modelId,
    selectModel,
    ttsModels,
    sttModels,
    sttModelInfo: sttModels.find((model) => model.id === modelId),
    /** The voice catalog read with the step's key: the models and voices it lists. */
    catalogStatus: kind === 'tts' ? ttsCatalogStatus : sttCatalogStatus,
  };
};
